import React, { useCallback, useState } from 'react'

import { Box, Grid, Stack } from '@mui/material'

import { ActionCall } from '@professocial/professocialTypes/types/commonTypes'
import ProfileImage from '../ProfileImage'
import LinkHeadline from '../LinkHeadline'
import SubHeadline from '../SubHeadline'
import Text from '../Text'

import { InstagramPost, InstagramPostComment, NetworkType } from '@3m5/sm_publish-frontend-api'
import { getDuration } from '../../../../../../util/getDuration'
import CommentDelete from './modules/CommentDelete'
import { useDispatch, useSelector } from 'react-redux'
import { DeleteInstagramPostCommentRequest } from '@3m5/sm_publish-frontend-api'
import { deleteInstagramPostCommentInitiated } from '../../../../../../store/instagram/instagram.actions'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import { useTranslation } from 'react-i18next'
import { PostingRootState } from '../../../../../../store/postingRoot.reducer'
import { namedRequestData } from '../../../../../../store/common/common.selectors'
import Preloader from '@professocial/professocialFormComponents/FormComponents/Preloader'

interface ISingleCommentProps {
  comment: InstagramPostComment
  post: InstagramPost
  networkPageId: string
  networkType?: NetworkType
  onChange?: (value: any) => void
}

const SingleInstagramComment: React.FC<ISingleCommentProps> = (props) => {
  const comment = props.comment
  const post = props.post
  const [isShown, setIsShown] = useState(false)
  const [showDeleteCommentWarning, setShowDeleteCommentWarning] = useState<boolean>(false)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const dispatchDeletePostComment = useCallback((params: DeleteInstagramPostCommentRequest) => dispatch(deleteInstagramPostCommentInitiated(params)), [dispatch])
  const actionOnDeleteComment: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, deleteInstagramPostCommentInitiated.type))

  const deletePost = () => {
    if (comment.networkCommentId) {
      const params: DeleteInstagramPostCommentRequest = {
        networkPageId: props.networkPageId,
        networkPostId: props.post.networkPostId,
        commentId: comment.networkCommentId,
      }
      comment.networkCommentId.indexOf('#') === -1 ? dispatchDeletePostComment(params) // only comments without # in id
        : props.onChange && props.onChange(params)
    }
  }
  const processing = actionOnDeleteComment?.inProgress && actionOnDeleteComment?.id === comment.networkCommentId
  return (
    <>
      <Grid
        container
        item xs={12}
        sx={{ pt: 2 }}
        onMouseEnter={() => setIsShown(true)}
        onMouseLeave={() => setIsShown(false)}
      >
        <Grid container>
          <Grid item xs='auto'>
            <ProfileImage
              data={`https://www.instagram.com/${comment.username}/?__a=1`}
            />
          </Grid>
          <Grid xs item>
            <Stack direction='row' alignItems='center'>
              <Stack direction='column'>
                <Box>
                  <LinkHeadline
                    networkPageId={props.networkPageId}
                    post={comment}
                    networkType={props.networkType}
                    value={{
                      url: `https://www.instagram.com/${comment.username}`,
                      label: comment.username,
                    }}
                  />
                  <Text
                    networkPageId={props.networkPageId}
                    post={post}
                    networkType={props.networkType}
                    value={comment.text}
                  />
                </Box>
                <Stack direction='row'>
                  <SubHeadline
                    networkPageId={props.networkPageId}
                    post={post}
                    networkType={props.networkType}
                    value={comment.timestamp && getDuration(comment.timestamp.getTime())}
                  />
                </Stack>
              </Stack>
              <Box marginLeft='auto' sx={{ minWidth: '50px' }}>
                {isShown && !processing && (
                  <CommentDelete
                    networkPageId={props.networkPageId}
                    post={post}
                    networkType={props.networkType}
                    onClickDelete={() => {
                      setShowDeleteCommentWarning(true)
                    }}
                  />
                )}
                {processing && <Preloader width='auto' minHeight='0' size={24} />}
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
      <DialogComponent
        type='error'
        title={t('networkContentMessages:deleteWarningTitle')}
        message={t('networkContentMessages:deleteCommentWarning')}
        open={showDeleteCommentWarning}
        confirmLabel={t('networkContentCommon:delete')}
        cancelLabel={t('networkContentCommon:cancel')}
        autoClose={false}
        requestActions
        onClose={() => setShowDeleteCommentWarning(false)}
        onCancel={() => setShowDeleteCommentWarning(false)}
        onConfirm={() => {
          setShowDeleteCommentWarning(false)
          deletePost()
        }}
      />
    </>
  )
}

export default SingleInstagramComment
